import { ChevronRightSharp } from '@mui/icons-material';
import { AccordionDetails, CircularProgress, styled, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { useFetchData } from '../../hooks/useFetchData';
import { selectFolders } from '../../redux/reducers/folderReducer';
import { StyledAccordion, StyledAccordionSummary } from '../../theme/CustomComponents';
import type {
  CategoryProgress,
  ProjectWithLastScanned,
  QuestionTypeTreeNode,
} from '../../Types/dealGPT';
import { DealGPTDiscoveryView, DealGPTQuestionStatus, ProjectRunStatus } from '../../Types/enums';
import { doubleEncodeUriComponent } from '../../util/formatterUtils';
import { fetchCategoryQuestionsPathUrl } from '../../util/routesUrls';
import { AnalyticsEvent, useAnalytics } from '../Providers/AnalyticsProvider';
import CategoryView from './CategoryView';
import StatusProgressBar from './StatusProgressBar';

const LoadingOverlay = styled('div')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(255, 255, 255, 0.8)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 9999,
});

export type CategoryAccordionProps = {
  runId: string;
  project: ProjectWithLastScanned;
  category: CategoryProgress;
  expandAll: boolean;
  discoveryView: DealGPTDiscoveryView;
  selectedStatusFilters: DealGPTQuestionStatus[];
  searchQuery: string;
  isIndexing: boolean;
  setShouldFetchRunProgress: (isRescan: boolean) => void;
};

const CategoryAccordion = ({
  project,
  category,
  expandAll,
  runId,
  discoveryView,
  selectedStatusFilters,
  searchQuery,
  isIndexing,
  setShouldFetchRunProgress,
}: CategoryAccordionProps): JSX.Element => {
  const [expandCategory, setExpandCategory] = useState<boolean>(expandAll);
  const [categoryQuestions, setCategoryQuestions] = useState<QuestionTypeTreeNode[]>([]);
  const { currentFolder } = useSelector(selectFolders);
  const indexId = currentFolder?.indexIds?.[0];
  const {
    data,
    startPolling: startPollingQuestions,
    stopPolling: stopPollingQuestions,
    loading: isLoadingQuestions,
  } = useFetchData<QuestionTypeTreeNode[]>({
    urlPath: fetchCategoryQuestionsPathUrl(
      project.id,
      doubleEncodeUriComponent(category.scope),
      doubleEncodeUriComponent(category.name),
      discoveryView === DealGPTDiscoveryView.SITE_VISIT_PREP
        ? [
            DealGPTQuestionStatus.MORE_INFO_NEEDED,
            DealGPTQuestionStatus.NOT_FOUND,
            DealGPTQuestionStatus.SKIPPED,
          ]
        : selectedStatusFilters,
      searchQuery,
      runId
    ),
    skip: !expandCategory,
  });

  const analytics = useAnalytics();

  const handleOpenCloseAccordion = () => {
    analytics.event(
      AnalyticsEvent.WM_IA_D_CATEGORY_ACCORDION,
      {
        id: project.id,
        value: `${category.name}=${(!expandCategory).toString()},Scope=${category.scope}`,
      },
      {
        projectId: project.id,
        runId,
        scope: category.scope,
        categoryName: category.name,
        numQuestionsInCategory: category.quantity,
        numQuestionsInCategoryAnswered: category.quantityAnswered,
        numQuestionsInCategoryMoreInfoNeeded: category.quantityMoreInfoNeeded,
        numQuestionsInCategoryNotFound: category.quantityNotFound,
        numQuestionsInCategorySkipped: category.quantitySkipped,
        numQuestionsInCategoryPending: category.quantityPending,
      }
    );
    setExpandCategory(!expandCategory);
  };

  useEffect(() => setCategoryQuestions(data ?? []), [data]);

  // Poll every 10 seconds while the project is processing and the accordion is expanded
  useEffect(() => {
    if (expandCategory && project.projectRunStatus === ProjectRunStatus.PROCESSING_QUESTIONS) {
      startPollingQuestions(10000);
    }

    return () => stopPollingQuestions();
  }, [project, expandCategory, startPollingQuestions, stopPollingQuestions]);

  useEffect(() => {
    setExpandCategory(expandAll);
  }, [expandAll]);

  useEffect(() => {
    setExpandCategory(false);
  }, [runId]);

  return (
    <StyledAccordion
      square
      disableGutters
      expanded={expandCategory}
      onChange={handleOpenCloseAccordion}
      id={`${category.scope}-${category.name}`}
    >
      <StyledAccordionSummary expandIcon={<ChevronRightSharp />}>
        <div>
          <Typography variant="subtitle2" sx={{ marginTop: '8px' }}>
            {category.name}
          </Typography>
          <Typography sx={{ fontStyle: 'italic' }}>{category.quantity} questions</Typography>
        </div>
        <div style={{ width: '33%', height: '20px', marginTop: '8px' }}>
          <StatusProgressBar
            quantity={category.quantity}
            quantityAnswered={category.quantityAnswered}
            quantityMoreInfoNeeded={category.quantityMoreInfoNeeded}
            quantityNotFound={category.quantityNotFound}
            quantitySkipped={category.quantitySkipped}
          />
        </div>
      </StyledAccordionSummary>
      <AccordionDetails style={{ position: 'relative' }}>
        {isLoadingQuestions ? (
          <LoadingOverlay>
            <CircularProgress />
          </LoadingOverlay>
        ) : !indexId ? (
          <Typography color="error" sx={{ padding: 2 }}>
            No valid index ID found. Please ensure a folder is selected.
          </Typography>
        ) : (
          <CategoryView
            indexToUse={project.indexToUse}
            indexId={indexId}
            projectId={project.id}
            runId={runId}
            setQuestions={setCategoryQuestions}
            questions={categoryQuestions}
            expandAll={expandAll}
            setShouldFetchRunProgress={setShouldFetchRunProgress}
            isLoadingProjectRescan={
              project.projectRunStatus === ProjectRunStatus.PROCESSING_QUESTIONS || isIndexing
            }
          />
        )}
      </AccordionDetails>
    </StyledAccordion>
  );
};

export default CategoryAccordion;
